import md5 from 'js-md5';

import {http} from './http/request';

const MB = 1024 * 1024;
const LARGE_PROJECT_FILE_UPLOAD_THRESHOLD = 10 * MB;
const PROJECT_FILE_CHUNK_SIZE = 0.3 * MB;

const hasOwnProperty = (object, property) => (
    Object.prototype.hasOwnProperty.call(object, property)
);

const unwrapResponse = function (response, fallbackMessage) {
    if (response && response.success === false) {
        throw new Error(response.message || fallbackMessage);
    }

    if (response && hasOwnProperty(response, 'result')) {
        return response.result;
    }

    return response;
};

const readBlobAsArrayBuffer = function (blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = () => reject(reader.error || new Error('File read failed'));
        reader.onabort = () => reject(new Error('File read aborted'));
        reader.readAsArrayBuffer(blob);
    });
};

const calculateFileMd5 = function (file, chunkSize = PROJECT_FILE_CHUNK_SIZE) {
    const hash = md5.create();
    const totalChunks = Math.ceil(file.size / chunkSize);

    const readChunk = chunkIndex => {
        if (chunkIndex >= totalChunks) {
            return Promise.resolve(hash.hex());
        }

        const start = chunkIndex * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        return readBlobAsArrayBuffer(file.slice(start, end))
            .then(buffer => {
                hash.update(new Uint8Array(buffer));
                return readChunk(chunkIndex + 1);
            });
    };

    return readChunk(0);
};

const shouldChunkUploadProjectFile = file => (
    Boolean(file && file.size > LARGE_PROJECT_FILE_UPLOAD_THRESHOLD)
);

const buildUploadMetadata = (file, fileMd5, chunkSize = PROJECT_FILE_CHUNK_SIZE) => ({
    chunkSize,
    file,
    fileId: fileMd5,
    fileName: file.name,
    md5: fileMd5,
    totalChunks: Math.ceil(file.size / chunkSize),
    totalSize: file.size
});

const appendUploadMetadata = function (formData, metadata) {
    formData.append('fileId', metadata.fileId);
    formData.append('fileName', metadata.fileName);
    formData.append('md5', metadata.md5);
};

const initChunkUpload = function (metadata) {
    const formData = new FormData();
    appendUploadMetadata(formData, metadata);

    return http.post('/chunk-upload/init', formData)
        .then(response => unwrapResponse(response, 'Chunk upload init failed'));
};

const uploadChunk = function (metadata, chunkIndex) {
    const start = chunkIndex * metadata.chunkSize;
    const end = Math.min(start + metadata.chunkSize, metadata.totalSize);
    const formData = new FormData();

    formData.append('file', metadata.file.slice(start, end), metadata.fileName);
    appendUploadMetadata(formData, metadata);
    formData.append('chunkIndex', String(chunkIndex));
    formData.append('totalChunks', String(metadata.totalChunks));
    formData.append('totalSize', String(metadata.totalSize));

    return http.post('/chunk-upload/chunk', formData)
        .then(response => unwrapResponse(response, 'Chunk upload failed'));
};

const mergeChunks = function (metadata) {
    const formData = new FormData();
    appendUploadMetadata(formData, metadata);

    return http.post('/chunk-upload/merge', formData)
        .then(response => unwrapResponse(response, 'Chunk merge failed'));
};

const getUploadedChunkSet = function (initResult) {
    if (!initResult || !Array.isArray(initResult.uploadedChunks)) {
        return new Set();
    }

    return new Set(initResult.uploadedChunks.map(chunkIndex => Number(chunkIndex)));
};

const uploadMissingChunks = function (metadata, uploadedChunks) {
    const uploadNext = chunkIndex => {
        if (chunkIndex >= metadata.totalChunks) {
            return Promise.resolve();
        }

        if (uploadedChunks.has(chunkIndex)) {
            return uploadNext(chunkIndex + 1);
        }

        return uploadChunk(metadata, chunkIndex)
            .then(() => uploadNext(chunkIndex + 1));
    };

    return uploadNext(0);
};

const uploadProjectFileChunks = function (file, fileMd5, chunkSize = PROJECT_FILE_CHUNK_SIZE) {
    const metadata = buildUploadMetadata(file, fileMd5, chunkSize);

    return initChunkUpload(metadata)
        .then(initResult => {
            if (initResult && initResult.skipUpload) {
                return Object.assign({}, initResult, {
                    fileId: metadata.fileId,
                    fileName: metadata.fileName,
                    md5: metadata.md5,
                    skipUpload: true
                });
            }

            return uploadMissingChunks(metadata, getUploadedChunkSet(initResult))
                .then(() => mergeChunks(metadata));
        });
};

const uploadProjectFileInChunks = function (file) {
    return calculateFileMd5(file)
        .then(fileMd5 => uploadProjectFileChunks(file, fileMd5));
};

export {
    LARGE_PROJECT_FILE_UPLOAD_THRESHOLD,
    PROJECT_FILE_CHUNK_SIZE,
    calculateFileMd5,
    shouldChunkUploadProjectFile,
    uploadProjectFileChunks,
    uploadProjectFileInChunks
};
